import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, X, Check, Plus, Eye, EyeOff } from "lucide-react";
import { API, useAuth } from "../App";
import { setRegionsCache, countByRegion } from "./useRegion";

// Admin-only inline editor: show/hide whole regions and individual towns from the area filters; add towns.
export const RegionFilterEditor = ({ regions, events = [] }) => {
  const { user } = useAuth();
  const { counts } = useMemo(() => countByRegion(events, regions), [events, regions]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState([]);
  const [adding, setAdding] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setDraft(regions.map((r) => ({ ...r, hidden_cities: r.hidden_cities || [], enabled: r.enabled !== false }))); }, [open, regions]);
  if (user?.role !== "admin") return null;

  const isHidden = (r, c) => r.hidden_cities.some((h) => h.toLowerCase() === c.toLowerCase());
  const toggleCity = (ri, c) => setDraft((d) => d.map((r, i) => i !== ri ? r : { ...r, hidden_cities: isHidden(r, c) ? r.hidden_cities.filter((h) => h.toLowerCase() !== c.toLowerCase()) : [...r.hidden_cities, c] }));
  const toggleRegion = (ri) => setDraft((d) => d.map((r, i) => (i === ri ? { ...r, enabled: !r.enabled } : r)));
  const addCity = (ri) => {
    const v = (adding[ri] || "").trim();
    if (!v) return;
    setDraft((d) => d.map((r, i) => (i === ri && !r.cities.some((c) => c.toLowerCase() === v.toLowerCase()) ? { ...r, cities: [...r.cities, v] } : r)));
    setAdding((a) => ({ ...a, [ri]: "" }));
  };
  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/admin/regions`, { regions: draft }, { withCredentials: true });
      setRegionsCache(r.data.regions);
      toast.success("Area filters updated");
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1 rounded-full border border-dashed border-[#1A1A1A]/40 px-2.5 py-1.5 text-[11px] font-bold text-[#5C5C5C] hover:text-[#1A1A1A] hover:border-[#1A1A1A]" title="Admin: show/hide areas & towns" data-testid="region-editor-open">
        <Pencil className="h-3 w-3" /> Edit filters
      </button>
    );
  }
  return (
    <div className="w-full rounded-2xl border-2 border-dashed border-[#1A1A1A]/30 bg-white p-4 mt-1" data-testid="region-editor">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="text-xs font-extrabold uppercase tracking-widest text-[#1A1A1A]">Admin · area filters — tap to show / hide</div>
        <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="text-[#5C5C5C] hover:text-[#1A1A1A]" data-testid="region-editor-close"><X className="h-4 w-4" /></button>
      </div>
      <div className="space-y-3">
        {draft.map((r, ri) => (
          <div key={r.id} className={`rounded-xl border p-3 ${r.enabled ? "border-[#E2E0DC]" : "border-[#E2E0DC] bg-[#F7F6F3] opacity-70"}`} data-testid={`region-editor-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <button type="button" onClick={() => toggleRegion(ri)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold text-white`} style={{ backgroundColor: r.enabled ? r.color : "#9A9A9A" }} data-testid={`region-editor-toggle-${r.id}`}>
                {r.enabled ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />} {r.label} {r.enabled ? "· shown" : "· hidden"}
              </button>
              <span className="text-[11px] text-[#5C5C5C]">{counts[r.id] || 0} upcoming</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {r.cities.map((c) => {
                const hid = isHidden(r, c);
                return (
                  <button key={c} type="button" onClick={() => toggleCity(ri, c)} aria-pressed={!hid}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold border transition-colors ${hid ? "line-through text-[#9A9A9A] border-[#F0EEEA] bg-[#FAFAF9]" : "text-[#1A1A1A] border-[#E2E0DC] hover:border-[#1A1A1A]"}`}
                    data-testid={`region-editor-city-${r.id}-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                    {c}
                  </button>
                );
              })}
              <span className="inline-flex items-center gap-1">
                <input value={adding[ri] || ""} onChange={(e) => setAdding((a) => ({ ...a, [ri]: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCity(ri); } }} placeholder="Add a town" className="h-7 w-28 rounded-full border border-dashed border-[#E2E0DC] px-2 text-[11px]" data-testid={`region-editor-add-${r.id}`} />
                <button type="button" onClick={() => addCity(ri)} className="h-7 w-7 rounded-full border border-[#E2E0DC] inline-flex items-center justify-center hover:border-[#1A1A1A]" aria-label="Add town" data-testid={`region-editor-add-btn-${r.id}`}><Plus className="h-3.5 w-3.5" /></button>
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button type="button" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full bg-[#1A1A1A] text-white px-4 py-2 text-xs font-bold hover:bg-black disabled:opacity-50" data-testid="region-editor-save"><Check className="h-3.5 w-3.5" /> {busy ? "Saving…" : "Save filters"}</button>
        <span className="text-[11px] text-[#5C5C5C]">Hidden areas disappear from the home map and the calendar for everyone. Music shows always stay in.</span>
      </div>
    </div>
  );
};
